/* eslint-disable react/prop-types */
import React from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

/**
 * Daily pauses vs alerts for one user across their run window (044).
 *
 * Dual axis on purpose: pause sessions and alerts can differ by an order of
 * magnitude, and a shared axis would flatten the smaller series into the baseline.
 * The legend names which axis each line belongs to; heights are NOT comparable
 * across series because the scales are independent.
 */

const PAUSE_COLOR = '#2e9b87'; // teal
const ALERT_COLOR = '#1f4d78'; // navy

export const TooltipContent = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="cus-tooltip">
      <div className="cus-tooltip__date">{label}</div>
      <div className="cus-tooltip__row">
        <span style={{ color: PAUSE_COLOR }}>■</span> Pauses: <b>{row.pauses ?? 0}</b>
      </div>
      <div className="cus-tooltip__row">
        <span style={{ color: ALERT_COLOR }}>■</span> Alerts: <b>{row.alerts ?? 0}</b>
      </div>
      <div className="cus-tooltip__region">
        {row.qualifying ? 'Qualifying day (alert + pause)' : 'Not a qualifying day'}
      </div>
    </div>
  );
};

const ContinuousUsageStreaksChart = ({ days = [], truncated = false }) => {
  const enough = Array.isArray(days) && days.length >= 2;

  return (
    <div className="au-panel" data-testid="cus-chart">
      <div className="au-panel__head">
        <h4 className="au-panel__title">Pauses vs alerts, per day</h4>
        <span className="au-spacer" />
        <div className="au-legend cus-legend" data-testid="cus-legend">
          <span><i style={{ background: PAUSE_COLOR }} />Pauses (left axis)</span>
          <span><i style={{ background: ALERT_COLOR }} />Alerts (right axis)</span>
        </div>
      </div>

      {truncated && (
        <p className="au-chart-note cus-truncated" data-testid="cus-chart-truncated">
          This user’s run window is longer than the chart can show. The earliest days
          have been trimmed; the table below is also limited to the shown window.
        </p>
      )}

      {enough ? (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={days} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f1" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} minTickGap={24} />
            <YAxis
              yAxisId="left"
              width={44}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              label={{ value: 'Pauses', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#94a3b8' }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={44}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              label={{ value: 'Alerts', angle: 90, position: 'insideRight', fontSize: 11, fill: '#94a3b8' }}
            />
            <Tooltip content={<TooltipContent />} />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="pauses"
              name="Pauses"
              stroke={PAUSE_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="alerts"
              name="Alerts"
              stroke={ALERT_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="au-chart-empty" data-testid="cus-chart-empty">
          Not enough data to plot this user’s window (need at least 2 days).
        </div>
      )}
    </div>
  );
};

export default ContinuousUsageStreaksChart;
